import { create } from 'zustand'
import api from './api'

const useStore = create((set, get) => ({
  token: null,
  userId: null,
  user: null,
  loadingUser: false,
  loadingTrials: false,
  listTrialsIncludeArchived: false,
  team: null,
  settings: {},
  roles: [],
  trials: [],

  loadSettingsFromStorage: () => {
    if (window.localStorage?.getItem('settings')) {
      set({ settings: JSON.parse(window.localStorage.getItem('settings')) })
    }
  },
  changeSetting: (key, value) => set(state => {
    const newSettings = Object.assign({}, state.settings)
    newSettings[key] = value
    window.localStorage.setItem('settings', JSON.stringify(newSettings))
    return { settings: newSettings }
  }),

  completeLogin: token => {
    if (!token) return
    const base64Token = token.split('.')[1].replace('-', '+').replace('_', '/')
    const tokenData = JSON.parse(window.atob(base64Token))
    api.accounts.storeToken(token)
    set({ userId: tokenData.sub, token, loadingUser: true })
    api.users.me(user => {
      api.users.update(user._id, { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })
      setTimeout(() => set({ loadingUser: false, user }), 500)
    })
  },
  completeLogout: () =>
    set({
      token: null,
      user: null,
      userId: null,
      loadingUser: false,
      trials: [],
      listTrialsIncludeArchived: false
    }),

  updateUser: (id, upd) => set(state => {
    return { user: Object.assign({}, { ...state.user }, upd) }
  }),
  addEmailSubscription: (topic) => set(state => {
    return { user: Object.assign({}, { ...state.user }, { subscriptions: { email: [...state.user.subscriptions.email, topic] } }) }
  }),
  removeEmailSubscription: (topic) => set(state => {
    return { user: Object.assign({}, { ...state.user }, { subscriptions: { email: state.user.subscriptions.email.filter(t => t !== topic) } }) }
  }),

  setTeam: team => set({ team }),
  updateTeam: (upd) => set(state => {
    return { team: Object.assign({}, { ...state.team }, upd) }
  }),

  trial: null,
  setTrials: trials => set({ trials }),
  setLoadingTrials: loading => set({ loadingTrials: loading }),
  setListTrialsIncludeArchived: value => set({ listTrialsIncludeArchived: value }),
  setTrial: trial => set(state => {
    const previousTrialId = state.trial?._id || null
    const nextTrialId = trial?._id || null
    const trialChanged = previousTrialId !== nextTrialId
    const baseState = {
      trial,
      dataTypes: trial?.dataTypes || [],
      stageGroups: trial?.stageGroups || [],
      stages: trial?.stages || [],
      dataTypeTags: trial?.dataTypeTags || [],
      sites: trial?.sites || [],
      groups: trial?.groups || [],
      anchors: trialChanged ? [] : state.anchors
    }
    if (trialChanged) {
      baseState.participantsTableFilters = {
        page: 1,
        sort: 'id',
        sortOrder: 'asc',
        searchTerm: '',
        selectedSiteId: null,
        groupIncludeIds: [],
        groupExcludeIds: [],
        groupNoneFilter: null,
        scheduleFilters: [],
        allocationFilters: [],
        joinFilters: []
      }
    }
    return baseState
  }),
  updateTrial: (id, upd) => set(state => {
    const newTrials = Object.assign([], state.trials)
    const index = newTrials?.findIndex(t => t._id === id)
    const existing = index !== -1 ? newTrials[index] : (state.trial?._id === id ? state.trial : {})
    const newTrial = { ...existing, ...upd }
    if (index === -1) newTrials.unshift(newTrial)
    else newTrials[index] = newTrial
    return { trials: newTrials, trial: state.trial?._id === id ? newTrial : state.trial }
  }),

  participantsTableFilters: {
    page: 1,
    sort: 'id',
    sortOrder: 'asc',
    searchTerm: '',
    selectedSiteId: null,
    groupIncludeIds: [],
    groupExcludeIds: [],
    groupNoneFilter: null,
    scheduleFilters: [],
    allocationFilters: [],
    joinFilters: []
  },
  setParticipantsTableFilters: (filters) => set(state => ({
    participantsTableFilters: {
      ...state.participantsTableFilters,
      ...filters
    }
  })),

  trialUsers: {},
  addTrialUser: (trialId, user) => set(state => {
    const newUsers = Object.assign({}, state.trialUsers)
    if (!newUsers[trialId]) newUsers[trialId] = []
    const index = newUsers[trialId].findIndex(u => u._id === user._id)
    if (index === -1) newUsers[trialId].push(user)
    else newUsers[trialId][index] = { ...newUsers[trialId][index], ...user }
    return { trialUsers: newUsers }
  }),
  removeTrialUser: (trialId, userId) => set(state => {
    const newUsers = Object.assign({}, state.trialUsers)
    if (!newUsers[trialId]) return state
    newUsers[trialId] = newUsers[trialId].filter(u => u._id !== userId)
    return { trialUsers: newUsers }
  }),
  getTrialUsers: trialId => {
    return get().trialUsers[trialId] || []
  },

  sites: [],
  addSite: site => set(state => {
    const newSites = Object.assign([], state.sites)
    const index = newSites?.findIndex(s => s._id === site._id)
    if (index === -1) newSites.push(site)
    else newSites[index] = { ...newSites[index], ...site }
    return { sites: newSites }
  }),
  deleteSite: siteId => set(state =>
    ({ sites: state.sites?.filter(s => s._id !== siteId) })
  ),

  groups: [],
  addGroup: group => set(state => {
    const newGroups = Object.assign([], state.groups)
    const index = newGroups?.findIndex(g => g._id === group._id)
    if (index === -1) newGroups.push(group)
    else newGroups[index] = { ...newGroups[index], ...group }
    return { groups: newGroups.sort((a, b) => a.index - b.index) }
  }),
  deleteGroup: groupId => set(state =>
    ({ groups: state.groups?.filter(g => g._id !== groupId) })
  ),

  dataTypes: [],
  addDataType: dataType => set(state => {
    if (!dataType?._id) {
      return state
    }
    const newDataTypes = Object.assign([], state.dataTypes)
    const index = newDataTypes?.findIndex(d => d._id === dataType._id)
    if (index === -1) newDataTypes.push(dataType)
    else newDataTypes[index] = { ...newDataTypes[index], ...dataType }
    return { dataTypes: newDataTypes }
  }),
  deleteDataType: dataTypeId => set(state =>
    ({ dataTypes: state.dataTypes?.filter(d => d._id !== dataTypeId) })
  ),

  anchors: [],
  setAnchors: anchors => set({ anchors }),
  addAnchor: anchor => set(state => {
    const list = Object.assign([], state.anchors)
    const index = list.findIndex(a => a._id === anchor._id)
    if (index === -1) list.push(anchor)
    else list[index] = { ...list[index], ...anchor }
    return { anchors: list }
  }),
  deleteAnchor: anchorId => set(state =>
    ({ anchors: state.anchors?.filter(a => a._id !== anchorId) })
  ),

  stages: [],
  addStage: stage => set(state => {
    const newStages = Object.assign([], state.stages)
    const index = newStages?.findIndex(s => s._id === stage._id)
    if (index === -1) newStages.push(stage)
    else newStages[index] = { ...newStages[index], ...stage }
    return { stages: newStages.sort((a, b) => a.index - b.index) }
  }),
  deleteStage: stageId => set(state =>
    ({ stages: state.stages?.filter(s => s._id !== stageId) })
  ),

  stageGroups: [],
  addStageGroup: group => set(state => {
    const newGroups = Object.assign([], state.stageGroups)
    const index = newGroups?.findIndex(g => g._id === group._id)
    if (index === -1) newGroups.push(group)
    else newGroups[index] = { ...newGroups[index], ...group }
    return { stageGroups: newGroups.sort((a, b) => a.index - b.index) }
  }),
  deleteStageGroup: groupId => set(state =>
    ({ stageGroups: state.stageGroups?.filter(g => g._id !== groupId) })
  ),

  dataTypeTags: [],
  addDataTypeTag: tag => set(state => {
    const newTags = Object.assign([], state.dataTypeTags)
    const index = newTags?.findIndex(t => t._id === tag._id)
    if (index === -1) newTags.push(tag)
    else newTags[index] = { ...newTags[index], ...tag }
    return { dataTypeTags: newTags }
  }),
  deleteDataTypeTag: tagId => set(state =>
    ({ dataTypeTags: state.dataTypeTags?.filter(t => t._id !== tagId) })
  ),

  participantNotes: [],
  addParticipantNote: note => set(state => {
    const newNotes = Object.assign([], state.participantNotes)
    const index = newNotes?.findIndex(n => n._id === note._id)
    if (index === -1) newNotes.push(note)
    else newNotes[index] = { ...newNotes[index], ...note }
    return { participantNotes: newNotes }
  }),
  /** Replace all notes for one participant (e.g. full list from API so deletes disappear). */
  setParticipantNotesForParticipant: (participantId, notes) => set(state => {
    const pid = String(participantId)
    const incoming = notes || []
    const rest = state.participantNotes?.filter(n => String(n.participant) !== pid) ?? []
    return { participantNotes: [...rest, ...incoming] }
  }),
  deleteParticipantNote: noteId => set(state =>
    ({ participantNotes: state.participantNotes?.filter(n => String(n._id) !== String(noteId)) })
  ),
  getParticipantNotesForParticipant: pId => get().participantNotes?.filter(n => String(n.participant) === String(pId))?.sort((n, n2) => n.createdAt < n2.createdAt),

  /** Video meetings keyed by `participant` on each row (same pattern as participantNotes). */
  participantMeetings: [],
  setParticipantMeetingsForParticipant: (participantId, meetings) => set((state) => {
    const pid = String(participantId)
    const incoming = meetings || []
    const rest = state.participantMeetings?.filter((m) => String(m.participant) !== pid) ?? []
    return { participantMeetings: [...rest, ...incoming] }
  }),
  upsertParticipantMeeting: (meeting) => set((state) => {
    if (!meeting?._id) return state
    const list = Object.assign([], state.participantMeetings ?? [])
    const idx = list.findIndex((m) => String(m._id) === String(meeting._id))
    if (idx === -1) list.unshift(meeting)
    else list[idx] = { ...list[idx], ...meeting }
    return { participantMeetings: list }
  }),
  deleteParticipantMeeting: (meetingId) => set((state) => ({
    participantMeetings: state.participantMeetings?.filter((m) => String(m._id) !== String(meetingId)) ?? []
  })),
  getParticipantMeetingsForParticipant: (pId) =>
    get().participantMeetings?.filter((m) => String(m.participant) === String(pId)) ?? [],

  participantEvents: [],
  setParticipantEventsForParticipant: (participantId, events) => set((state) => {
    const pid = String(participantId)
    const incoming = events || []
    const rest = state.participantEvents?.filter((e) => String(e.participant) !== pid) ?? []
    return { participantEvents: [...rest, ...incoming] }
  }),
  getParticipantEventsForParticipant: (pId) =>
    get().participantEvents?.filter((e) => String(e.participant) === String(pId)) ?? [],

  addRole: role => set(state => {
    const newRoles = Object.assign([], state.roles)
    const index = newRoles?.findIndex(r => r._id === role._id)
    if (index === -1) newRoles.push(role)
    else newRoles[index] = { ...newRoles[index], ...role }
    return { roles: newRoles }
  }),
  removeRole: roleId => set(state =>
    ({ roles: state.roles?.filter(r => r._id !== roleId) })
  ),
  getRolesForTrial: trialId => {
    return get().roles?.filter(r => r.trial === trialId)
  }
}))

export default useStore
